import { useMutation, useQuery } from '@tanstack/react-query'
import {
	ArrowDownIcon,
	ArrowUpIcon,
	BarChart,
	Button,
	CheckCircleIcon,
	CollapsibleSection,
	Label,
	MetricCard,
	ModuleHeader,
	PieChartIcon,
	Select,
	SpinnerWithBackground,
	Text,
	UsersIcon,
} from '@viapeople/components'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { sessionStore } from '../../sharedStores/sessionStore'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'

export const LoopResultsPage = () => {
	const { loopId } = useParams<{
		loopId: string
	}>()
	const { user } = sessionStore()
	const organizationId = user?.organizationId || ''
	const navigate = useNavigate()
	const toast = useToast()
	const [_selectedExecutionIds, _setSelectedExecutionIds] = useState<string[]>([])
	const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv')

	// Fetch loop results
	const {
		data: loopResults,
		isLoading,
		isError,
	} = useQuery({
		...trpc.client.loops.getLoopResults.queryOptions({
			loopId: loopId || '',
			organizationId: organizationId || '',
			includeIndividualResponses: false,
		}),
		enabled: !!loopId && !!organizationId,
	})

	// Fetch comparison data
	const { data: comparisonData } = useQuery({
		...trpc.client.loops.getResultsComparison.queryOptions({
			loopId: loopId || '',
			organizationId: organizationId || '',
			executionIds: _selectedExecutionIds.length > 0 ? _selectedExecutionIds : undefined,
		}),
		enabled: !!loopId && !!organizationId,
	})

	// Export mutation
	const exportMutation = useMutation({
		// @ts-expect-error - WIP: exportResults query type not yet finalized
		...trpc.client.loops.exportResults.mutationOptions(),
		onSuccess: (data: string, variables: { format: 'csv' | 'json' }) => {
			const blob = new Blob([data], {
				type: variables.format === 'csv' ? 'text/csv' : 'application/json',
			})
			const url = window.URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = `loop-results-${loopResults?.loopName}-${new Date().toISOString().split('T')[0]}.${variables.format}`
			document.body.appendChild(a)
			a.click()
			window.URL.revokeObjectURL(url)
			document.body.removeChild(a)
			toast.showToast('Results exported successfully')
		},
		onError: (error: Error) => {
			toast.showToast(error.message || 'Failed to export results', 'error')
		},
	})

	// Calculate overall statistics
	const overallStats = useMemo(() => {
		if (!loopResults?.executions || loopResults.executions.length === 0) {
			return null
		}

		const totalParticipants = loopResults.executions.reduce((sum, exec) => sum + exec.statistics.totalParticipants, 0)
		const totalCompleted = loopResults.executions.reduce((sum, exec) => sum + exec.statistics.completedResponses, 0)
		const avgResponseRate =
			loopResults.executions.reduce((sum, exec) => sum + exec.statistics.responseRate, 0) /
			loopResults.executions.length

		const ratingsWithValues = loopResults.executions
			.filter((exec) => exec.statistics.averageRating !== null)
			.map((exec) => exec.statistics.averageRating as number)
		const avgRating =
			ratingsWithValues.length > 0
				? ratingsWithValues.reduce((sum, rating) => sum + rating, 0) / ratingsWithValues.length
				: null

		return {
			totalExecutions: loopResults.totalExecutions,
			totalParticipants,
			totalCompleted,
			avgResponseRate: Math.round(avgResponseRate * 100) / 100,
			avgRating: avgRating !== null ? Math.round(avgRating * 100) / 100 : null,
		}
	}, [loopResults])

	// Prepare chart data for response rate trends
	const responseRateTrendData = useMemo(() => {
		if (!comparisonData?.executions || comparisonData.executions.length === 0) {
			return []
		}

		return comparisonData.executions.map((exec, index) => ({
			label: `Iteration ${index + 1}`,
			value: exec.responseRate,
			color: exec.responseRate >= 80 ? '#10b981' : exec.responseRate >= 60 ? '#f59e0b' : '#ef4444',
		}))
	}, [comparisonData])

	// Prepare chart data for average rating trends
	const ratingTrendData = useMemo(() => {
		if (!comparisonData?.executions || comparisonData.executions.length === 0) {
			return []
		}

		return comparisonData.executions
			.filter((exec) => exec.averageRating !== null)
			.map((exec, index) => ({
				label: `Iteration ${index + 1}`,
				value: exec.averageRating as number,
				color: '#3b82f6',
			}))
	}, [comparisonData])

	const handleExport = (executionId: string) => {
		if (!organizationId) return
		exportMutation.mutate({
			// @ts-expect-error - WIP: exportResults mutation type not yet finalized
			executionId,
			organizationId,
			format: exportFormat,
		})
	}

	const handleViewDetails = (executionId: string) => {
		navigate(`/loops/${loopId}/results/execution/${executionId}`)
	}

	const getTrendIcon = (trend: string) => {
		switch (trend) {
			case 'improving':
				return <ArrowUpIcon className='w-5 h-5 text-green-600' />
			case 'declining':
				return <ArrowDownIcon className='w-5 h-5 text-red-600' />
			case 'stable':
				return <span className='w-5 h-5 text-gray-600 flex items-center justify-center'>−</span>
			default:
				return null
		}
	}

	const getTrendLabel = (trend: string) => {
		switch (trend) {
			case 'improving':
				return 'Improving'
			case 'declining':
				return 'Declining'
			case 'stable':
				return 'Stable'
			case 'insufficient_data':
				return 'Insufficient Data'
			default:
				return 'Unknown'
		}
	}

	if (isLoading) {
		return (
			<main className='w-full p-6'>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	if (isError || !loopResults) {
		return (
			<main className='w-full p-6'>
				<ModuleHeader
					breadcrumb={[
						{ label: 'Loops', to: '/loops' },
						{ label: 'Results', to: '#' },
					]}
					title='Results'
				/>
				<div className='mt-6 text-center'>
					<Text variant='medium' weight='semibold' className='mb-2'>
						Unable to load results
					</Text>
					<Text variant='small' className='text-gray-600'>
						There was a problem loading the results for this loop. Please try again later.
					</Text>
				</div>
			</main>
		)
	}

	return (
		<main className='w-full p-6'>
			<ModuleHeader
				breadcrumb={[
					{ label: 'Loops', to: '/loops' },
					{ label: loopResults.loopName, to: `/loops/${loopId}` },
					{ label: 'Results', to: '#' },
				]}
				title={`${loopResults.loopName} - Results & Analytics`}
			/>

			{loopResults.loopDescription && (
				<div className='mt-4 bg-blue-50 p-4 rounded-lg'>
					<Text variant='small' className='text-blue-900'>
						{loopResults.loopDescription}
					</Text>
				</div>
			)}

			{/* Overall Statistics */}
			{overallStats && (
				<div className='mt-6'>
					<Text variant='medium' weight='semibold' className='mb-4'>
						Overall Statistics
					</Text>
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
						<MetricCard icon={PieChartIcon} title='Total Iterations' value={overallStats.totalExecutions.toString()} />
						<MetricCard icon={UsersIcon} title='Total Participants' value={overallStats.totalParticipants.toString()} />
						<MetricCard icon={CheckCircleIcon} title='Avg Response Rate' value={`${overallStats.avgResponseRate}%`} />
						{overallStats.avgRating !== null && loopResults.ratingQuestionEnabled && (
							<MetricCard
								icon={PieChartIcon}
								title='Avg Rating'
								value={`${overallStats.avgRating} / ${loopResults.ratingScale || 5}`}
							/>
						)}
					</div>
				</div>
			)}

			{/* Trends */}
			{comparisonData && comparisonData.executions.length > 1 && (
				<div className='mt-8'>
					<div className='flex items-center justify-between mb-4'>
						<Text variant='medium' weight='semibold'>
							Trends Across Iterations
						</Text>
					</div>

					<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
						{/* Response Rate Trend */}
						<div className='border border-neutral-200 rounded-lg p-6'>
							<div className='flex items-center justify-between mb-4'>
								<Text variant='small' weight='semibold'>
									Response Rate Trend
								</Text>
								<div className='flex items-center gap-2'>
									{getTrendIcon(comparisonData.trend.responseRateTrend)}
									<Text variant='xsmall' className='text-gray-600'>
										{getTrendLabel(comparisonData.trend.responseRateTrend)}
									</Text>
								</div>
							</div>
							{responseRateTrendData.length > 0 && (
								<BarChart
									data={responseRateTrendData}
									orientation='vertical'
									showPercentages={true}
									showValues={true}
									height={300}
								/>
							)}
						</div>

						{/* Average Rating Trend */}
						{loopResults.ratingQuestionEnabled && ratingTrendData.length > 0 && (
							<div className='border border-neutral-200 rounded-lg p-6'>
								<div className='flex items-center justify-between mb-4'>
									<Text variant='small' weight='semibold'>
										Average Rating Trend
									</Text>
									<div className='flex items-center gap-2'>
										{getTrendIcon(comparisonData.trend.ratingTrend)}
										<Text variant='xsmall' className='text-gray-600'>
											{getTrendLabel(comparisonData.trend.ratingTrend)}
										</Text>
									</div>
								</div>
								<BarChart
									data={ratingTrendData}
									orientation='vertical'
									showPercentages={false}
									showValues={true}
									height={300}
								/>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Iteration Results */}
			<div className='mt-8'>
				<div className='flex items-center justify-between mb-4'>
					<Text variant='medium' weight='semibold'>
						Results by Iteration
					</Text>
					<div className='flex gap-2'>
						<Select
							value={exportFormat}
							onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json')}
							options={[
								{ value: 'csv', label: 'CSV' },
								{ value: 'json', label: 'JSON' },
							]}
						/>
					</div>
				</div>

				<div className='space-y-4'>
					{loopResults.executions.map((execution, index) => (
						<CollapsibleSection
							key={execution.executionId}
							title={`Iteration #${loopResults.executions.length - index}`}
							defaultOpen={index === 0}>
							<div className='p-4 space-y-4'>
								{/* Execution Info */}
								<div className='grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg'>
									<div>
										<Text variant='xsmall' className='text-gray-600'>
											Launched
										</Text>
										<Text variant='small' weight='semibold'>
											{new Date(execution.launchedAt).toLocaleDateString()}
										</Text>
									</div>
									<div>
										<Text variant='xsmall' className='text-gray-600'>
											Status
										</Text>
										<Label color={execution.status === 'active' ? 'green' : 'default'} className='w-20!'>
											{execution.status === 'active' ? 'Active' : 'Closed'}
										</Label>
									</div>
									<div>
										<Text variant='xsmall' className='text-gray-600'>
											Response Rate
										</Text>
										<Text variant='small' weight='semibold'>
											{execution.statistics.responseRate}% ({execution.statistics.completedResponses}/
											{execution.statistics.totalParticipants})
										</Text>
									</div>
									{execution.statistics.averageRating !== null && loopResults.ratingQuestionEnabled && (
										<div>
											<Text variant='xsmall' className='text-gray-600'>
												Average Rating
											</Text>
											<Text variant='small' weight='semibold'>
												{execution.statistics.averageRating} / {loopResults.ratingScale || 5}
											</Text>
										</div>
									)}
								</div>

								{/* Rating Distribution */}
								{loopResults.ratingQuestionEnabled && execution.statistics.ratingDistribution.length > 0 && (
									<div>
										<Text variant='small' weight='semibold' className='mb-2'>
											Rating Distribution
										</Text>
										<BarChart
											data={execution.statistics.ratingDistribution.map((dist) => ({
												label: `${dist.rating} ${dist.rating === 1 ? 'star' : 'stars'}`,
												value: dist.count,
												percentage: dist.percentage,
											}))}
											orientation='horizontal'
											showPercentages={true}
											showValues={true}
											height={Math.max(200, execution.statistics.ratingDistribution.length * 60)}
										/>
									</div>
								)}

								{/* Text Responses */}
								{loopResults.textQuestionEnabled && execution.textResponses.length > 0 && (
									<div>
										<Text variant='small' weight='semibold' className='mb-2'>
											Text Responses ({execution.textResponses.length})
										</Text>
										<div className='space-y-2 max-h-96 overflow-y-auto'>
											{execution.textResponses.slice(0, 5).map((response, idx) => (
												// biome-ignore lint/suspicious/noArrayIndexKey: text responses may have duplicate content
												<div key={idx} className='border-l-4 border-blue-500 pl-4 py-2 bg-gray-50'>
													<Text variant='small'>{response}</Text>
												</div>
											))}
											{execution.textResponses.length > 5 && (
												<Button
													variant='secondary'
													size='small'
													onClick={() => handleViewDetails(execution.executionId)}>
													View all {execution.textResponses.length} responses
												</Button>
											)}
										</div>
									</div>
								)}

								{/* Actions */}
								<div className='flex gap-2 pt-4 border-t'>
									<Button variant='secondary' size='small' onClick={() => handleViewDetails(execution.executionId)}>
										View Details
									</Button>
									<Button
										variant='secondary'
										size='small'
										onClick={() => handleExport(execution.executionId)}
										disabled={exportMutation.isPending}>
										<ArrowDownIcon className='w-4 h-4 mr-2' />
										Export {exportFormat.toUpperCase()}
									</Button>
								</div>
							</div>
						</CollapsibleSection>
					))}
				</div>
			</div>

			{/* Empty State */}
			{loopResults.executions.length === 0 && (
				<div className='border border-neutral-200 rounded-lg p-12 text-center mt-6'>
					<PieChartIcon className='w-12 h-12 mx-auto mb-4 text-neutral-400' />
					<Text variant='h6' weight='semibold' className='mb-2'>
						No iterations yet
					</Text>
					<Text variant='small' className='text-neutral-500'>
						This loop has not launched any iterations yet. Results will appear here once iterations are launched.
					</Text>
				</div>
			)}
		</main>
	)
}
